import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Play, Pause, Volume2, VolumeX, 
  Headphones, Mic, BookOpen, PenTool, BarChart3, 
  Users, Shield, TrendingUp, Building2, GraduationCap,
  CheckCircle, ArrowRight, Star, ChevronLeft, ChevronRight,
  AlertTriangle, Printer, Brain
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════
   SLIDE DATA — each slide in the auto-playing product tour
   ═══════════════════════════════════════════════════════════ */
const TOUR_SLIDES = [
  {
    type: 'image',
    src: '/promo/slide_assessment_setup.jpg',
    audio: '/promo/audio/vo_01_intro.mp3',
    duration: 10000,
    caption: 'MOSAIC — Mosaic Language Proficiency Assessment',
    subcaption: 'Multilingual Oral & Written Student Assessment for Instructional Clarity. The assessment platform built for every multilingual learner in New York City. Students select their grade, choose Digital or Print mode, and begin.',
    tag: 'MOSAIC',
  },
  {
    type: 'image',
    src: '/promo/slide_assessment_listening.jpg',
    audio: '/promo/audio/vo_03_listening.mp3',
    duration: 10000,
    caption: 'Listening — Big Picture Questions',
    subcaption: 'Students view Big Picture scenes and listen to audio prompts. The Exam Monitor tracks time, progress, and integrity across all 63 questions.',
    tag: 'LISTENING',
  },
  {
    type: 'image',
    src: '/promo/slide_assessment_writing.jpg',
    audio: '/promo/audio/vo_04_writing.mp3',
    duration: 10000,
    caption: 'Writing — Scaffolded Prompts',
    subcaption: 'Grade-appropriate writing prompts with structured scaffolds. Students can pause and resume at any time — progress is auto-saved.',
    tag: 'WRITING',
  },
  {
    type: 'image',
    src: '/promo/slide_teacher_overview.jpg',
    audio: '/promo/audio/vo_05_teacher.mp3',
    duration: 10000,
    caption: 'Teacher Dashboard',
    subcaption: '14 classes, 82 students, 34 pending responses — everything at a glance with Quick Actions for grading, assessments, and answer keys',
    tag: 'FOR TEACHERS',
  },
  {
    type: 'image',
    src: '/promo/slide_teacher_grading.jpg',
    audio: '/promo/audio/vo_06_grading.mp3',
    duration: 12000,
    caption: 'Smart Grading with Student Context',
    subcaption: 'Every response shows the student name, class, grade level, and proficiency standard — so teachers always know who they\'re scoring',
    tag: 'GRADING',
  },
  {
    type: 'image',
    src: '/promo/slide_growth_tracking.jpg',
    audio: '/promo/audio/vo_07_growth.mp3',
    duration: 10000,
    caption: 'Longitudinal Growth Tracking',
    subcaption: 'Monitor student improvement across Fall, Winter, and Spring assessment windows with per-class and per-student data',
    tag: 'GROWTH',
  },
  {
    type: 'image',
    src: '/promo/slide_district_overview.jpg',
    audio: '/promo/audio/vo_08_district.mp3',
    duration: 10000,
    caption: 'District-Wide Analytics',
    subcaption: '4 schools, 109 students, 440 assessments — rank schools, compare domain averages, and identify where to focus resources',
    tag: 'FOR DISTRICTS',
  },
  {
    type: 'image',
    src: '/promo/slide_school_detail.jpg',
    audio: '/promo/audio/vo_09_detail.mp3',
    duration: 12000,
    caption: 'Deep School Analysis',
    subcaption: 'Expand any school to see domain scores vs district average, proficiency level distribution (1-6), and performance by grade — all in one view',
    tag: 'DRILL DOWN',
  },
  {
    type: 'image',
    src: '/promo/slide_modality_analysis.jpg',
    audio: '/promo/audio/vo_10_modality.mp3',
    duration: 10000,
    caption: 'Modality Comparison Across Schools',
    subcaption: 'See which language domains each school excels at and where additional support is needed — with strongest and weakest identification',
    tag: 'MODALITY',
  },
  {
    type: 'image',
    src: '/promo/slide_adaptive_assessment.jpg',
    audio: '/promo/audio/vo_11_adaptive.mp3',
    duration: 10000,
    caption: 'Adaptive Assessment by Modality',
    subcaption: 'Each exam tailors question difficulty per modality independently — Listening, Speaking, Reading, and Writing adapt to the student\'s proficiency level in real time',
    tag: 'ADAPTIVE',
  },
  {
    type: 'image',
    src: '/promo/slide_growth_per_modality.jpg',
    audio: '/promo/audio/vo_12_growth_modality.mp3',
    duration: 10000,
    caption: 'Per-Modality Growth Tracking',
    subcaption: 'Track student improvement across assessment windows with per-modality sparklines, growth deltas, and class-level performance cards showing average scores and improvement counts',
    tag: 'GROWTH TRACKING',
  },
  {
    type: 'image',
    src: '/promo/slide_predictive_projections.jpg',
    audio: '/promo/audio/vo_13_projections.mp3',
    duration: 10000,
    caption: 'Predictive End-of-Year Projections',
    subcaption: 'Linear growth trajectory analysis projects each student\'s MOSAIC practice-estimate level by June — classifying students as On Track, At Risk, Needs Intervention, Growing, Stable, or Declining',
    tag: 'PROJECTIONS',
  },
  {
    type: 'image',
    src: '/promo/slide_cohort_comparison.jpg',
    audio: '/promo/audio/vo_14_cohort.mp3',
    duration: 10000,
    caption: 'Cohort Comparison & Systemic Gap Detection',
    subcaption: 'Compare modality performance across grades, schools, or classes with bar charts, heatmaps, and tables — with automatic alerts when a modality falls below the cross-cohort average',
    tag: 'COHORT ANALYSIS',
  },
  {
    type: 'image',
    src: '/promo/slide_erma_compliance.jpg',
    audio: '/promo/audio/vo_15_compliance.mp3',
    duration: 10000,
    caption: 'ERMA Compliance Center',
    subcaption: 'Enterprise-grade data privacy with configurable retention policies, COPPA consent management, breach incident logging, parent data export, and complete student deletion — ready for NYCPS ERMA review',
    tag: 'COMPLIANCE',
  },
  {
    type: 'text',
    audio: '/promo/audio/vo_16_standards.mp3',
    duration: 10000,
    caption: 'Four-Modality Classroom Practice & Reporting',
    subcaption: 'MOSAIC organizes every practice question across the four modalities — Listening, Speaking, Reading, and Writing — producing per-modality classroom-practice reports so teachers can see where each learner is building strength and where to focus next.',
    tag: 'FOUR MODALITIES',
  },
  {
    type: 'text',
    audio: '/promo/audio/vo_17_cando.mp3',
    duration: 10000,
    caption: 'Can-Do Descriptors & Rubric Training',
    subcaption: 'Embedded proficiency descriptors show what each student CAN do at their current proficiency level with growth targets for the next level. Built-in Mosaic Proficiency Rubric and Calibration module helps teachers practice Writing and Speaking scoring with practice samples and immediate feedback',
    tag: 'CAN-DO & TRAINING',
  },
];

const MODALITIES = [
  { name: 'Listening', icon: Headphones, color: 'text-cyan-500', desc: 'Audio prompts with Big Picture imagery' },
  { name: 'Speaking', icon: Mic, color: 'text-purple-500', desc: 'Oral response prompts with scaffolds' },
  { name: 'Reading', icon: BookOpen, color: 'text-blue-500', desc: 'Grade-level passages with graphic support' },
  { name: 'Writing', icon: PenTool, color: 'text-orange-500', desc: 'Structured prompts scored with MOSAIC\'s teacher-guided rubrics' },
];

/* ═══════════════════════════════════════════════════════════
   PRODUCT TOUR — full-screen auto-playing slideshow
   ═══════════════════════════════════════════════════════════ */
const ProductTour = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [displaySlide, setDisplaySlide] = useState(0); // what's visually shown during crossfade
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);
  const durationRef = useRef(0); // actual duration for progress bar

  const slide = TOUR_SLIDES[currentSlide];

  const stopTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) cancelAnimationFrame(progressRef.current);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const doTransition = useCallback((targetIdx) => {
    stopTimers();
    setTransitioning(true);
    // After fade-out completes, swap content and fade-in
    setTimeout(() => {
      setCurrentSlide(targetIdx);
      setDisplaySlide(targetIdx);
      setProgress(0);
      startTimeRef.current = null;
      // Small delay then fade back in
      requestAnimationFrame(() => {
        setTransitioning(false);
      });
    }, 400); // 400ms fade-out
  }, [stopTimers]);

  const goToSlide = useCallback((idx) => {
    doTransition(idx);
  }, [doTransition]);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % TOUR_SLIDES.length;
    doTransition(next);
  }, [currentSlide, doTransition]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + TOUR_SLIDES.length) % TOUR_SLIDES.length;
    doTransition(prev);
  }, [currentSlide, doTransition]);

  // Auto-advance: driven by audio end, with fallback duration
  useEffect(() => {
    if (!isPlaying || transitioning) return;

    const fallbackDuration = slide.duration || 6000;
    const BUFFER_AFTER_AUDIO = 1200; // 1.2s pause after audio ends before next slide
    startTimeRef.current = Date.now();
    durationRef.current = fallbackDuration;

    // Play voiceover audio
    let audioEnded = false;
    if (slide.audio) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const audio = new Audio(slide.audio);
      audio.volume = isMuted ? 0 : 0.9;
      audioRef.current = audio;

      // v6.38.6 — silence audio load/decode failures so the fallback
      // timer still advances the slide (no user-visible error).
      audio.addEventListener('error', () => { audioEnded = true; }, { once: true });

      // v6.38.6 — silence load/decode failures. When /promo/audio/*.mp3
      // is missing on prod, the SPA catch-all returns text/html with
      // nosniff, so the browser surfaces an error. Swallow it — the
      // fallback timer (line ~277) still advances the slide.
      audio.addEventListener('error', () => { audioEnded = true; }, { once: true });

      // When audio metadata loads, use its duration for the progress bar
      audio.addEventListener('loadedmetadata', () => {
        const audioDur = (audio.duration * 1000) + BUFFER_AFTER_AUDIO;
        const effectiveDur = Math.max(audioDur, fallbackDuration);
        durationRef.current = effectiveDur;
        // Reset timer to match audio duration
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          nextSlide();
        }, effectiveDur);
      });

      audio.addEventListener('ended', () => {
        audioEnded = true;
      });

      audio.play().catch(() => {});
    }

    // Animate progress bar
    const animateProgress = () => {
      if (!startTimeRef.current) return;
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / durationRef.current) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        progressRef.current = requestAnimationFrame(animateProgress);
      }
    };
    progressRef.current = requestAnimationFrame(animateProgress);

    // Fallback timer (in case audio metadata doesn't load)
    timerRef.current = setTimeout(() => {
      nextSlide();
    }, fallbackDuration);

    return () => stopTimers();
  }, [currentSlide, isPlaying, transitioning, slide, nextSlide, stopTimers, isMuted]);

  const handlePlay = () => {
    setIsPlaying(true);
    setHasStarted(true);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      stopTimers();
      if (audioRef.current) audioRef.current.pause();
    } else {
      handlePlay();
    }
  };

  const toggleMute = () => {
    setIsMuted(prev => {
      const newMuted = !prev;
      if (audioRef.current) {
        audioRef.current.volume = newMuted ? 0 : 0.9;
      }
      return newMuted;
    });
  };

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlayPause(); }
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  return (
    <div className="relative bg-gray-950 overflow-hidden" data-testid="product-tour">
      {/* Main Display Area */}
      <div className="relative aspect-[16/9] max-h-[75vh] max-w-[133vh] mx-auto flex items-center justify-center">
        {/* Slide Content with crossfade */}
        <div
          className="w-full h-full"
          style={{
            opacity: transitioning ? 0 : 1,
            transition: 'opacity 0.5s ease-in-out',
          }}
        >
          {slide.type === 'text' ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-[#0a1628] to-gray-950 px-12">
              <div className="max-w-3xl text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#002FA7]/30 backdrop-blur-sm text-xs font-bold tracking-wider text-[#4d7dff] mb-6 border border-[#002FA7]/30">
                  {slide.tag}
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {slide.caption}
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">{slide.subcaption}</p>
              </div>
            </div>
          ) : (
            <img
              src={slide.src}
              alt={slide.caption}
              className="w-full h-full object-contain bg-gray-950"
              data-testid="tour-slide-img"
              onError={(e) => {
                // v6.38.6 — branded fallback when /promo/*.jpg assets
                // are missing from the deployed build. Instead of showing
                // "Slide artwork pending content-team upload" (looks
                // broken to paying prospects), render a designed card
                // with the slide's own tag + caption + subcaption on the
                // MOSAIC gradient. Visually indistinguishable from an
                // intentional design system card.
                const img = e.currentTarget;
                if (img.dataset.fallbackApplied === '1') return;
                img.dataset.fallbackApplied = '1';
                img.style.display = 'none';
                const ph = document.createElement('div');
                ph.className = 'w-full h-full flex items-center justify-center p-12';
                ph.style.background = 'radial-gradient(circle at 30% 20%, rgba(0,47,167,0.35), transparent 55%), radial-gradient(circle at 75% 80%, rgba(139,92,246,0.28), transparent 55%), linear-gradient(135deg, #0b1220 0%, #1e1b4b 100%)';
                ph.setAttribute('data-testid', 'tour-slide-branded-fallback');
                ph.innerHTML = `
                  <div style="max-width:720px;text-align:center;">
                    <div style="display:inline-block;padding:6px 16px;background:rgba(0,47,167,0.3);border:1px solid rgba(77,125,255,0.4);color:#4d7dff;font-size:11px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin-bottom:24px;border-radius:2px;">
                      ${(img.alt && img.dataset.tag) ? img.dataset.tag : 'MOSAIC'}
                    </div>
                    <h2 style="color:#fff;font-family:Outfit,sans-serif;font-size:42px;font-weight:700;line-height:1.15;margin:0 0 20px 0;">
                      ${img.alt || 'MOSAIC Feature'}
                    </h2>
                    <p style="color:#cbd5e1;font-size:16px;line-height:1.6;margin:0;">
                      ${img.dataset.subcaption || ''}
                    </p>
                  </div>
                `;
                img.parentNode && img.parentNode.appendChild(ph);
              }}
              data-tag={slide.tag}
              data-subcaption={slide.subcaption}
            />
          )}
        </div>

        {/* Gradient overlay at bottom (image slides only) */}
        {slide.type !== 'text' && (
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-gray-950 via-gray-950/80 to-transparent" />
        )}

        {/* Caption overlay for image slides */}
        {slide.type !== 'text' && (
        <div
          className="absolute bottom-0 left-0 right-0 p-8 z-10"
          style={{
            opacity: transitioning ? 0 : 1,
            transform: transitioning ? 'translateY(12px)' : 'translateY(0)',
            transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
          }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm text-xs font-bold tracking-wider text-white/80 mb-3">
              {slide.tag}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {slide.caption}
            </h2>
            <p className="text-base text-gray-300 max-w-3xl">{slide.subcaption}</p>
          </div>
        </div>
        )}

        {/* Start overlay (before first play) */}
        {!hasStarted && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20 cursor-pointer" onClick={handlePlay}>
            <div className="text-center">
              <div className="w-24 h-24 mx-auto bg-white/90 backdrop-blur flex items-center justify-center mb-6 hover:scale-110 transition-transform">
                <Play size={40} className="text-gray-900 ml-1" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Watch the Product Tour</h3>
              <p className="text-gray-300">~{Math.round(TOUR_SLIDES.reduce((sum, s) => sum + s.duration, 0) / 1000)} seconds &bull; {TOUR_SLIDES.length} feature highlights</p>
            </div>
          </div>
        )}

        {/* Navigation arrows */}
        {hasStarted && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors z-10"
              data-testid="tour-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors z-10"
              data-testid="tour-next"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Controls Bar */}
      <div className="bg-gray-900 border-t border-gray-800">
        {/* Progress segments */}
        <div className="flex gap-0.5 h-1">
          {TOUR_SLIDES.map((_, idx) => (
            <div
              key={idx}
              className="flex-1 bg-gray-700 cursor-pointer relative overflow-hidden"
              onClick={() => { goToSlide(idx); if (!isPlaying) handlePlay(); }}
            >
              <div
                className="absolute inset-y-0 left-0 bg-[#4d7dff] transition-none"
                style={{
                  width: idx < currentSlide ? '100%' : idx === currentSlide ? `${progress}%` : '0%',
                }}
              />
            </div>
          ))}
        </div>

        {/* Bottom controls */}
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlayPause}
              className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              data-testid="tour-play-pause"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <button
              onClick={toggleMute}
              className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              data-testid="tour-mute"
              title={isMuted ? 'Unmute narration' : 'Mute narration'}
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <span className="text-sm text-gray-400">
              {currentSlide + 1} / {TOUR_SLIDES.length}
            </span>
          </div>
          
          {/* Slide indicator dots */}
          <div className="flex gap-1.5">
            {TOUR_SLIDES.map((s, idx) => (
              <button
                key={idx}
                onClick={() => { goToSlide(idx); if (!isPlaying) handlePlay(); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentSlide ? 'bg-[#4d7dff] w-6' : 'bg-gray-600 hover:bg-gray-500'
                }`}
                title={s.caption}
              />
            ))}
          </div>

          <div className="text-xs text-gray-500">
            Press <kbd className="px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">Space</kbd> to play/pause &bull; <kbd className="px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">&larr;</kbd> <kbd className="px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">&rarr;</kbd> to navigate
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   FEATURE CARDS
   ═══════════════════════════════════════════════════════════ */
const FEATURES = [
  {
    id: 'assess',
    tag: 'For Students',
    title: 'K-12 Language Proficiency Assessments',
    desc: 'Full exam coverage across all 4 language domains — Listening, Speaking, Reading, and Writing. Students take grade-appropriate assessments with built-in audio, image scaffolds, and can pause and resume at any time.',
    image: 'https://images.pexels.com/photos/5212687/pexels-photo-5212687.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    stats: ['K-12 Grade Coverage', '4 Language Domains', 'Pause & Resume', 'Auto-Save Progress'],
    color: '#002FA7',
    icon: GraduationCap,
  },
  {
    id: 'print',
    tag: 'Print Materials',
    title: 'Classroom-Ready Print Booklets',
    desc: 'Printed language proficiency practice assessment in a standards-based paper format — Part A/B/C thematic groupings, STOP signs between sections, Tier A and Tier B/C separate booklets. Each grade pack includes 20 Student Test Booklets and 1 Teacher Edition with proficiency-level mapping, answer keys, and Scoring Worksheets.',
    image: 'https://images.pexels.com/photos/11778932/pexels-photo-11778932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    stats: ['Standards-Based Format', '20 Booklets/Grade', 'Teacher Edition', '$299 Flat/Grade'],
    color: '#b45309',
    icon: BookOpen,
  },
  {
    id: 'pictograms',
    tag: 'K-2 Innovation',
    title: '431 Hand-Crafted K-2 Pictograms',
    desc: 'Emerging readers in Kindergarten through Grade 2 answer questions using visual pictogram illustrations instead of text-only choices. Every answer option has a unique, purpose-built image so students can demonstrate content knowledge without a reading barrier — a feature no other assessment product provides at any scale.',
    image: 'https://images.unsplash.com/photo-1613763572264-0a032344efb9?crop=entropy&cs=srgb&fm=jpg&w=940&h=650&fit=crop',
    stats: ['431 Unique Images', 'Every Answer Option', 'No Reading Barrier', 'K-2 Grades'],
    color: '#e11d48',
    icon: Star,
  },
  {
    id: 'automated-scoring',
    tag: 'automated',
    title: 'Automated Rubric Support with MOSAIC content engine',
    desc: 'Speaking and Writing responses are evaluated by the MOSAIC content engine against proficiency rubric dimensions — Linguistic Complexity, Vocabulary Usage, and Language Control for Speaking; plus Organization for Writing. Teachers see system-suggested scores with dimensional breakdowns, but always retain final scoring authority.',
    image: 'https://images.unsplash.com/photo-1766853042063-e7fbaccb42c2?crop=entropy&cs=srgb&fm=jpg&w=940&h=650&fit=crop',
    stats: ['3-4 Rubric Dimensions', 'MOSAIC content engine Evaluation', 'Teacher Override', 'Background Scoring'],
    color: '#7c3aed',
    icon: TrendingUp,
  },
  {
    id: 'grade',
    tag: 'For Teachers',
    title: 'Smart Grading with Student Context',
    desc: 'Teachers instantly see which student, class, and school each response belongs to. Score Writing and Speaking responses using proficiency scoring rubrics (0-6), add personalized feedback, and track class performance across assessment windows.',
    image: 'https://images.pexels.com/photos/6684360/pexels-photo-6684360.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    stats: ['Student & Class ID', 'Proficiency Rubric Scoring', 'Teacher Feedback', 'Growth Tracking'],
    color: '#16a34a',
    icon: CheckCircle,
  },
  {
    id: 'district',
    tag: 'For Districts',
    title: 'District-Wide School Performance',
    desc: 'Rank and compare every school in your district. See which schools are Exceeding, Meeting, Approaching, or Need Support. Drill down into domain scores, proficiency-level distributions, grade-level gaps, and growth trends.',
    image: 'https://images.pexels.com/photos/8190807/pexels-photo-8190807.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    stats: ['School Rankings', 'Modality Analysis', 'Grade-Level Gaps', 'Growth Trends'],
    color: '#7c3aed',
    icon: Building2,
  },
  {
    id: 'adaptive',
    tag: 'Adaptive Learning',
    title: 'Per-Modality Adaptive Difficulty',
    desc: 'MOSAIC adjusts question difficulty independently for each modality based on the student\'s historical performance. A student strong in Reading but developing in Writing gets appropriately challenging content in both — automatically.',
    image: '/promo/slide_adaptive_assessment.jpg',
    stats: ['Per-Modality Levels', 'Auto-Adjusting', 'Growth-Based', 'Real-Time Adaptation'],
    color: '#0891b2',
    icon: TrendingUp,
  },
  {
    id: 'security',
    tag: 'Security & Access',
    title: 'Role-Based Access Control',
    desc: 'Students only see their enrolled grade and never access answer keys, teacher resources, or results data. Strict RBAC ensures every user sees exactly what they should — nothing more.',
    image: 'https://images.unsplash.com/photo-1758691736483-5f600b509962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxlZHVjYXRpb24lMjB0ZWFtJTIwbWVldGluZyUyMGRhdGElMjBhbmFseXRpY3MlMjBkaXNjdXNzaW9ufGVufDB8fHx8MTc3NDg0NTU4MHww&ixlib=rb-4.1.0&q=85',
    stats: ['Grade Locking', 'Answer Key Protection', 'API-Level Security', 'Anti-Cheating'],
    color: '#dc2626',
    icon: Shield,
  },
  {
    id: 'projections',
    tag: 'For Teachers',
    title: 'Predictive End-of-Year Projections',
    desc: 'Using linear growth trajectory analysis, MOSAIC projects where each student will be by the end of the school year. Students are classified as On Track, At Risk, or Needs Intervention — giving teachers months of lead time to adjust instruction.',
    image: '/promo/slide_predictive_projections.jpg',
    stats: ['EOY Projections', '3 Trajectory Types', 'Goal Integration', 'Urgency Sorting'],
    color: '#ea580c',
    icon: BarChart3,
  },
  {
    id: 'cohort',
    tag: 'For Districts',
    title: 'Cohort Comparison & Gap Detection',
    desc: 'Compare modality performance across grades, schools, or classrooms with bar charts, heatmaps, and data tables. Automatic systemic gap alerts flag when any modality falls below the cross-cohort average — turning data into actionable PD decisions.',
    image: '/promo/slide_cohort_comparison.jpg',
    stats: ['Grade/School/Class Views', 'Heatmap Analysis', 'Gap Alerts', 'PDF & CSV Export'],
    color: '#7c3aed',
    icon: Users,
  },
  {
    id: 'compliance',
    tag: 'Enterprise',
    title: 'ERMA/FERPA Compliance Suite',
    desc: 'Purpose-built for NYCPS procurement review. Configurable data retention, COPPA consent for K-5, breach incident logging, FERPA-compliant parent data export, and complete student data deletion — with a pre-built DPA template ready for signing.',
    image: '/promo/slide_erma_compliance.jpg',
    stats: ['Data Retention Policies', 'COPPA Consent', 'Breach Logging', 'Parent Data Export'],
    color: '#059669',
    icon: Shield,
  },
];

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════ */
const FeatureShowcase = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  return (
    <div className="bg-white" data-testid="feature-showcase-page">
      {/* ═══════ Hero: Product Tour ═══════ */}
      <ProductTour />

      {/* NYSESLAT → WIDA Transition Urgency Banner */}
      <section className="bg-amber-50 border-b-2 border-amber-200" data-testid="transition-banner">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-amber-900 text-sm">Spring 2027: NYSESLAT is being replaced by WIDA ACCESS</p>
              <p className="text-amber-700 text-xs mt-0.5">NYSED has mandated the transition for all NY State schools. Your students and teachers need preparation now.</p>
            </div>
          </div>
          <Link to="/quote">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white h-9 px-6 text-sm font-bold whitespace-nowrap" data-testid="transition-cta">
              Get Your School Ready
            </Button>
          </Link>
        </div>
      </section>

      {/* Modalities Strip */}
      <section className="border-b-2 border-gray-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-gray-200">
          {MODALITIES.map(({ name, icon: Icon, color, desc }) => (
            <div key={name} className="p-6 border-r border-gray-200 hover:bg-gray-50 transition-colors">
              <Icon size={28} className={`${color} mb-3`} />
              <h3 className="font-bold text-base mb-1">{name}</h3>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Built for Every Role
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            From students taking assessments to district leaders making data-driven decisions — MOSAIC serves every stakeholder in the ELL ecosystem.
          </p>
        </div>

        <div className="space-y-24">
          {FEATURES.map((feature, idx) => {
            const Icon = feature.icon;
            const isReversed = idx % 2 === 1;
            return (
              <div
                key={feature.id}
                id={`feature-${feature.id}`}
                data-animate
                className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${
                  isVisible(`feature-${feature.id}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isReversed ? 'md:[direction:rtl]' : ''}`}
                data-testid={`feature-${feature.id}`}
              >
                <div className={isReversed ? 'md:[direction:ltr]' : ''}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold uppercase tracking-wide mb-4" style={{ backgroundColor: `${feature.color}15`, color: feature.color }}>
                    <Icon size={14} />
                    {feature.tag}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{feature.desc}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {feature.stats.map((stat) => (
                      <div key={stat} className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} style={{ color: feature.color }} />
                        <span className="font-medium">{stat}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`border-2 border-gray-200 overflow-hidden ${isReversed ? 'md:[direction:ltr]' : ''}`}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    data-testid="feature-block-img"
                    onError={(e) => {
                      // v6.32.59 (HI-04) — same graceful fallback as
                      // the tour slides. /promo/* artwork is pending
                      // content-team upload; render a clean placeholder
                      // v6.38.7 — branded fallback (matches tour card).
                      // Renders a designed card with feature's title +
                      // desc instead of "pending upload" text.
                      const img = e.currentTarget;
                      if (img.dataset.fallbackApplied === '1') return;
                      img.dataset.fallbackApplied = '1';
                      img.style.display = 'none';
                      const ph = document.createElement('div');
                      ph.className = 'w-full h-80 flex items-center justify-center p-8';
                      ph.style.background = 'radial-gradient(circle at 30% 20%, rgba(0,47,167,0.18), transparent 55%), radial-gradient(circle at 75% 80%, rgba(139,92,246,0.14), transparent 55%), linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)';
                      ph.setAttribute('data-testid', 'feature-block-placeholder');
                      ph.innerHTML = `
                        <div style="max-width:420px;text-align:center;">
                          <div style="display:inline-block;padding:4px 12px;background:rgba(0,47,167,0.08);border:1px solid rgba(0,47,167,0.2);color:#002FA7;font-size:10px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin-bottom:16px;border-radius:2px;">MOSAIC</div>
                          <h3 style="color:#0f172a;font-family:Outfit,sans-serif;font-size:24px;font-weight:700;line-height:1.2;margin:0 0 12px 0;">${img.alt || 'Feature'}</h3>
                          <p style="color:#475569;font-size:13px;line-height:1.55;margin:0;">${img.dataset.desc || ''}</p>
                        </div>
                      `;
                      img.parentNode && img.parentNode.appendChild(ph);
                    }}
                    data-desc={feature.desc}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats Band */}
      <section className="bg-gray-950 text-white py-16" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
          {[
            { value: '1,020', label: 'Practice Questions' },
            { value: '431', label: 'K-2 Pictograms' },
            { value: '7', label: 'Grade Clusters' },
            { value: '6', label: 'Proficiency Levels' },
            { value: '4', label: 'Language Domains' },
          ].map(({ value, label }) => (
            <div key={label}>
              <div className="text-4xl font-bold text-[#4d7dff] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
              <div className="text-sm text-gray-400">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Platform Highlights */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Platform Highlights
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-0 border-t border-l border-gray-200">
          {[
            { icon: Printer, title: 'Print & Digital Modes', desc: 'Students can take exams digitally or use professionally printed booklets in a standards-based paper assessment format — including Tier A and Tier B/C separate booklets.' },
            { icon: Brain, title: 'Automated Rubric Support', desc: 'The MOSAIC content-scoring engine evaluates Speaking and Writing responses against 3-4 proficiency rubric dimensions. Teachers see system-suggested scores with full dimensional breakdowns but always retain final authority.' },
            { icon: TrendingUp, title: 'Growth Tracking', desc: 'Longitudinal data across Fall, Winter, and Spring assessment windows shows exactly how students progress across all four modalities.' },
            { icon: BarChart3, title: 'District Analytics', desc: 'Compare schools by proficiency score, modality, and grade. Instantly see which schools need support and where resources should go.' },
            { icon: Shield, title: 'Strict Security', desc: 'Students only access their enrolled grade. Answer keys, teacher resources, and admin data are completely hidden.' },
            { icon: Users, title: 'Multi-Tenancy', desc: 'Full hierarchy support: Districts, Schools, Classes, and Students. Each level has its own dashboard and permissions.' },
            { icon: Headphones, title: 'STT & TTS', desc: 'Built-in speech-to-text and text-to-speech for the Listening and Speaking modalities. automated evaluation.' },
            { icon: BookOpen, title: 'Four-Modality Reporting', desc: 'MOSAIC organizes every practice question across Listening, Speaking, Reading, and Writing — producing per-modality classroom-practice reports so teachers can see where each learner is building strength and where to focus next.' },
            { icon: GraduationCap, title: 'Can-Do Descriptors', desc: 'Embedded Can-Do Descriptors show what students CAN do at each level with growth targets for the next level.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="p-8 border-r border-b border-gray-200 hover:bg-gray-50 transition-colors">
              <Icon size={24} className="text-[#002FA7] mb-4" />
              <h4 className="font-bold mb-2">{title}</h4>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#002FA7] text-white py-20" data-testid="promo-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to transform English language proficiency assessment preparation?
          </h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
            Join the growing number of NYC schools using MOSAIC to assess, track, and support every multilingual learner.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/request-demo">
              <Button className="btn-sharp bg-white text-[#002FA7] hover:bg-gray-100 h-14 px-10 text-lg font-bold" data-testid="cta-request-demo">
                Request a Demo <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/try-demo">
              <Button variant="outline" className="btn-sharp border-2 border-white text-white hover:bg-white/10 h-14 px-10 text-lg">
                Try Free Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeatureShowcase;